// eslint-disable-next-line no-restricted-imports
import { type SortDescriptor } from "@progress/kendo-data-query";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { setSelected } from "commonComponents/utilities/Grid/common/BTGridSelectCell/BTGridSelectCell";
import {
    type GridColumn,
    type IGridRowData,
    type IGroupedRowData,
    isGridRowData,
} from "commonComponents/utilities/Grid/GridContainer.types";

import "./GridCardView.less";

import { GridCard } from "./GridCard";
import { GridCardSortBar } from "./GridCardSortBar";
import { useCardColumns } from "./GridCardView.utils";
import { GridCardViewActionBar } from "./GridCardViewActionBar";

export interface IGridCardViewConfig {
    /** Opt-in flag. When false/undefined, the grid never switches to card view. */
    enabled: boolean;
    /**
     * Column IDs (matched against `column.id`, the integer enum value as a
     * string) that should render with a "Label: Value" prefix. Cards default
     * to label-less rows — values are usually self-evident — so opt in here
     * only for fields that need context. Use named constants rather than raw
     * strings so the intent stays readable. Per design: keep this list short.
     */
    labeledColumnIds?: readonly string[];
    /**
     * Column IDs (matched against `column.id`) pinned to the bottom of the card,
     * rendered after all other non-link columns, in the order listed here. Use
     * for fields that should read as a card footer (e.g. a total). Keep this
     * list short.
     */
    trailingColumnIds?: readonly string[];
}

interface IGridCardViewProps {
    /**
     * Every available column for this grid (including ones the user has hidden).
     * Card content and sort options are filtered from this list to columns
     * flagged `IsAlwaysIncluded` on the server, so cards have a stable,
     * server-defined shape regardless of how the user has customized their
     * saved view.
     */
    columnsListAll: GridColumn<unknown>[];
    rowData: (IGridRowData | IGroupedRowData)[];
    sortingData: SortDescriptor[];
    onSortChange: (newSort: SortDescriptor[]) => void;
    config: IGridCardViewConfig;
    isSortDisabled?: boolean;
    hasSelectableRows?: boolean;
    singleSelect?: boolean;
    onSelectChange?: (dataItem: IGridRowData | IGroupedRowData) => void;
    onHeaderSelectChange?: (isSelected: boolean) => void;
    /** pass formik setFieldValue */
    setFieldValue?: (field: string, value: unknown) => void;
    /**
     * Fires once after the cards have rendered (rows are present) plus a single
     * animation frame, signaling it is safe to restore scroll position. Mirrors
     * {@link IBTGridProps.onInitialRenderComplete} so card-view lists restore scroll
     * the same way the grid does. Not fired when there are no rows to display.
     */
    onInitialRenderComplete?: () => void;
}

type SelectableRow = IGridRowData | IGroupedRowData;

// `setSelected`'s declared signature returns `any | IGroupedRowData` because
// of Kendo-era overloads. Wrap it so consumers stay strongly-typed.
const setRowSelected = <T extends SelectableRow>(row: T, checked: boolean): T =>
    setSelected(row, checked) as T;

/**
 * Mobile-first card list that replaces `<BTGrid>` when `gridCardView` is
 * enabled and the trigger fires. Card content is driven by the server via
 * the `IsAlwaysIncluded` column flag — every column flagged on the backend
 * appears in the card, regardless of which columns the user has chosen to
 * show or hide in their desktop saved view.
 */
export const GridCardView: React.FC<IGridCardViewProps> = ({
    columnsListAll,
    rowData,
    sortingData,
    onSortChange,
    config,
    isSortDisabled,
    hasSelectableRows,
    singleSelect,
    onSelectChange,
    onHeaderSelectChange,
    setFieldValue,
    onInitialRenderComplete,
}) => {
    const cardColumns = useCardColumns(columnsListAll, config.trailingColumnIds);

    const flatRows = useMemo(
        () => rowData.filter((row): row is IGridRowData => isGridRowData(row)),
        [rowData],
    );

    // Fire once the cards are actually in the DOM (rows present) plus one frame to
    // let layout settle, so a consumer can restore scroll position. Unlike the
    // grid, the card view renders every row (no virtualization), so once rows are
    // present the full page height exists and a window scroll restore lands exactly.
    const hasFiredInitialRender = useRef(false);
    useEffect(() => {
        if (hasFiredInitialRender.current || !onInitialRenderComplete || flatRows.length === 0) {
            return;
        }
        hasFiredInitialRender.current = true;
        const rafId = window.requestAnimationFrame(() => onInitialRenderComplete());
        return () => window.cancelAnimationFrame(rafId);
    }, [onInitialRenderComplete, flatRows.length]);

    const handleRowSelectToggle = useCallback(
        (row: IGridRowData, rowIndex: number, checked: boolean) => {
            setFieldValue?.(`rowData[${rowIndex}].isSelected`, checked);
            // Pass the pre-click row, mirroring BTGrid.handleSelectionChange which passes
            // event.dataItem before mutation. gridStateManager.onRowSelected reads isSelected
            // to decide add (!isSelected) vs remove (isSelected) — so it must see the prior state.
            onSelectChange?.(row);
        },
        [onSelectChange, setFieldValue],
    );

    const handleSelectAllToggle = useCallback(
        (checked: boolean) => {
            const updatedRows = rowData.map((row) => setRowSelected(row, checked));
            setFieldValue?.("rowData", updatedRows);
            if (onHeaderSelectChange) {
                onHeaderSelectChange(checked);
            } else if (onSelectChange) {
                rowData.forEach((row) => {
                    if (row.isSelected !== checked) {
                        onSelectChange(row);
                    }
                });
            }
        },
        [rowData, onHeaderSelectChange, onSelectChange, setFieldValue],
    );

    const showActionBar = hasSelectableRows === true && !singleSelect;

    return (
        <div className="GridCardView" data-testid="GridCardView">
            <GridCardSortBar
                columnsList={cardColumns}
                sortingData={sortingData}
                onSortChange={onSortChange}
                isDisabled={isSortDisabled}
            />
            {showActionBar && (
                <GridCardViewActionBar
                    rowData={flatRows}
                    onSelectAllToggle={handleSelectAllToggle}
                />
            )}
            <div className="GridCardView-list">
                {flatRows.length === 0 ? (
                    <div className="GridCardView-empty" data-testid="GridCardView-empty">
                        No results found
                    </div>
                ) : (
                    flatRows.map((row, index) => (
                        <GridCard
                            key={row.id}
                            row={row}
                            rowIndex={index}
                            cardColumns={cardColumns}
                            labeledColumnIds={config.labeledColumnIds}
                            isSelectable={hasSelectableRows}
                            onSelectToggle={handleRowSelectToggle}
                        />
                    ))
                )}
            </div>
        </div>
    );
};
